import React, { useState, useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useRecoilValue } from 'recoil';
import { proxySetIdState } from 'venn-state';
import { intersectionBy } from 'lodash';
import {
  getProxyOptionsForFund,
  getLayeredProxyOptionsForFund,
  type ProxyOption,
  type ProxyTypeEnum,
  type FundReturnsRange,
  type ProxyMeta,
  getCustomProxyOptionForFund,
} from 'venn-api';
import { Icon, Notifications, NotificationType, ShimmerBlock, Tooltip } from 'venn-ui-kit';
import {
  createProxyOptionsKey,
  getFormattedProxyType,
  logExceptionIntoSentry,
  ReturnsCalculations,
  useFetchApiConditionally,
  useHasFF,
  useQueries,
  withSuspense,
} from 'venn-utils';
import { cn } from '@/lib/utils';

import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import { getCategoryProxyTypeMetadata, sortCategoryOptions, type SelectedProxy } from './utils';
import type { FundToBulkProxy } from '../types';
import { BulkProxySearch } from './components/BulkProxySearch';
import InterpolationSnapshot from '../../proxy-cell/InterpolationSnapshot';
import { BulkProxyLegend } from './BulkProxyLegend';

interface FirstLayerProxyMeta {
  proxyId: string;
  proxyType: ProxyTypeEnum;
  numLags: number;
  extrapolate: boolean;
}

interface BulkCategoryOptionProps {
  investments: FundToBulkProxy[];
  proxyType?: ProxyTypeEnum;
  selectedProxy: SelectedProxy | null;
  numLags?: number;
  extrapolate: boolean;
  onSelect: (selected: SelectedProxy | null, numLags?: number) => void;
  rawInvestmentReturnsRanges: FundReturnsRange[];
  selectedProxyType: ProxyTypeEnum | undefined;
  firstLayerProxyMeta?: FirstLayerProxyMeta;
}

interface RadioOption {
  element: JSX.Element;
  value: string;
  index: number;
  disabled?: boolean;
  showRadio?: boolean;
  label?: string;
}

const ADDITIONAL_OPTIONS = 2; // the radio group has two additional options we're manually including: search input and a text category

const TextCategory = ({ singleProxy, proxyType }: { singleProxy: boolean; proxyType?: ProxyTypeEnum }) => {
  const hasLayeredProxyFF = useHasFF('layered_proxy_picker_ff');

  if (!proxyType) {
    return null;
  }

  const proxyTypeMetadata = getCategoryProxyTypeMetadata(proxyType);
  const lowerCaseProxyTypeName = getFormattedProxyType(proxyType).toLowerCase();

  return (
    <div
      className={`border border-solid border-venn-grey-30 border-b-0 ${hasLayeredProxyFF ? 'border-x-0 px-[36px]' : ''} p-[12px] flex flex-col`}
    >
      <div className="flex items-center justify-between">
        <span className="font-bold leading-[1.3]">Or select a category below</span>
        {singleProxy && <BulkProxyLegend />}
      </div>
      <span className="text-venn-grey-80 text-[12px]">
        Categories represent public indices that can be used for {lowerCaseProxyTypeName}.{' '}
        <a
          className="font-bold text-venn-dark-blue"
          target="_blank"
          rel="noopener noreferrer"
          href={proxyTypeMetadata.helpLink}
        >
          Learn more
        </a>
      </span>
    </div>
  );
};

const RadioCategory = ({ singleProxy, proxyOption }: { singleProxy: boolean; proxyOption: ProxyOption }) => {
  const { Colors } = useTheme();

  const { warning, rawCumulativeReturn, interpolatedCumulativeReturn } = proxyOption;
  const { displayName: label, indexName: description } = proxyOption.category;

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-col ml-[1.5px]">
        <div className="flex items-center gap-[4.5px]">
          {singleProxy && warning === 'BELOW_THRESHOLD' && (
            <Tooltip content="This proxy sits outside our recommended threshold for this investment." usePortal>
              <Icon className="fa-diamond-exclamation text-venn-orange" />
            </Tooltip>
          )}
          <span className="leading-[1.3]">{label}</span>
          {singleProxy && warning !== 'BELOW_THRESHOLD' && (
            <Badge variant="success" className="flex items-center gap-[3px]">
              <Icon type="check" />
              Within Threshold
            </Badge>
          )}
        </div>
        <span className="text-[12px] text-venn-grey-80">{description}</span>
      </div>
      {singleProxy && (
        <div className="max-w-[288px]">
          <InterpolationSnapshot
            primarySeries={ReturnsCalculations.toDataPoints(rawCumulativeReturn ?? [])}
            overlaySeries={ReturnsCalculations.toDataPoints(interpolatedCumulativeReturn ?? [])}
            subjectLineColor={Colors.HighlightDark}
            markerRadius={1.5}
          />
        </div>
      )}
    </div>
  );
};

const ShimmerFallback = () => {
  return (
    <div className="mt-[9px]">
      {Array.from({ length: 8 }, (_, index) => (
        <ShimmerBlock
          height={70}
          key={`shimmer-${index}`}
          className="w-full border border-solid border-venn-grey-30 mb-0"
        />
      ))}
    </div>
  );
};

const InternalBulkCategoryOptions = ({
  investments,
  proxyType,
  numLags = 0,
  extrapolate,
  selectedProxy,
  onSelect,
  selectedProxyType,
  rawInvestmentReturnsRanges,
  firstLayerProxyMeta,
}: BulkCategoryOptionProps) => {
  const { Colors } = useTheme();
  const hasLayeredProxyFF = useHasFF('layered_proxy_picker_ff');

  const sortedProxyOptions = useProxyOptions(investments, proxyType, numLags, firstLayerProxyMeta);
  const singleProxy = investments.length === 1;

  const options: RadioOption[] = useMemo(
    () =>
      sortedProxyOptions.map((option, index) => ({
        element: <RadioCategory singleProxy={singleProxy} proxyOption={option} />,
        value: option.category.indexId,
        index,
        disabled: singleProxy && !option.proxiedCumulativeReturn,
        label: option.category.displayName,
      })),
    [sortedProxyOptions, singleProxy],
  );

  const isCategoryProxy = useIsCategoryProxy(selectedProxy, options);

  const [customSelectedProxy, setCustomSelectedProxy] = useState<SelectedProxy | null>(
    !isCategoryProxy ? selectedProxy : null,
  );

  const customProxyOption = useFetchApiConditionally(
    customSelectedProxy != null && investments.length > 0 && !!investments[0]?.id,
    getCustomProxyOptionForFund,
    investments[0]?.id ?? '',
    customSelectedProxy?.id ?? '',
    proxyType,
    numLags ?? 0,
    extrapolate,
  )?.result;

  const [selectedIndex, setSelectedIndex] = useState<number>(() =>
    !!selectedProxy && isCategoryProxy
      ? options.findIndex((option) => option.value === selectedProxy.id) + ADDITIONAL_OPTIONS // +2 for the search and text options
      : 0,
  );

  const onSelectCustomProxy = useCallback(
    (customProxy: SelectedProxy) => {
      onSelect(customProxy);
      setCustomSelectedProxy(customProxy);
    },
    [onSelect],
  );

  const combinedOptions: RadioOption[] = useMemo(() => {
    const allCategoryIds = sortedProxyOptions.map((option) => option.category.indexId);

    const searchOption = {
      element: (
        <div className="flex flex-col gap-[6px]">
          <div className="flex items-center gap-[4.5px]">
            {customProxyOption?.warning === 'BELOW_THRESHOLD' && (
              <Tooltip content="This proxy sits outside our recommended threshold for this investment." usePortal>
                <Icon className="text-venn-error" type="exclamation-triangle" />
              </Tooltip>
            )}
            <span className="font-bold leading-[1.3]">Search for a custom investment</span>
          </div>
          <div className="flex flex-wrap items-start justify-end lg:justify-between gap-[24px] relative">
            {/* Select control width is being overwritten to prevent search results from being truncated when width of parent decreases  */}
            <div className="flex flex-col gap-[6px] flex-grow w-full sm:[&_.select\_\_control]:w-[calc(100%-theme(space.96)-theme(space.8))]">
              <BulkProxySearch
                selectedProxy={customSelectedProxy}
                setSelectedProxy={onSelectCustomProxy}
                rawInvestmentReturnsRanges={rawInvestmentReturnsRanges}
                selectedProxyType={selectedProxyType}
                investments={investments}
                disabled={selectedIndex !== 0}
                excludedInvestmentIds={allCategoryIds}
              />
            </div>
            {singleProxy && (
              <div className="w-[288px] h-[24px] sm:absolute right-0 top-0">
                <InterpolationSnapshot
                  primarySeries={ReturnsCalculations.toDataPoints(customProxyOption?.rawCumulativeReturn ?? [])}
                  overlaySeries={ReturnsCalculations.toDataPoints(
                    customProxyOption?.interpolatedCumulativeReturn ?? [],
                  )}
                  subjectLineColor={Colors.HighlightDark}
                  markerRadius={1.5}
                />
              </div>
            )}
          </div>
        </div>
      ),
      value: 'custom',
      label: 'Search for a custom investment',
    } as RadioOption;

    const textOption = {
      element: <TextCategory singleProxy={singleProxy} proxyType={proxyType} />,
      value: 'text',
      showRadio: false,
    } as RadioOption;
    return [searchOption, textOption, ...options].map((option, index) => ({ ...option, index }));
  }, [
    selectedIndex,
    sortedProxyOptions,
    options,
    proxyType,
    rawInvestmentReturnsRanges,
    selectedProxyType,
    customSelectedProxy,
    customProxyOption,
    investments,
    onSelectCustomProxy,
    singleProxy,
    Colors.HighlightDark,
  ]);

  const onCategoryChange = useCallback(
    (value: string) => {
      const selectedOptionIndex = combinedOptions.findIndex((option) => option.value === value);
      setSelectedIndex(selectedOptionIndex);

      if (value === 'custom') {
        onSelect(customSelectedProxy, 0);
      } else {
        const selectedOption = sortedProxyOptions.find((option) => option.category.indexId === value);
        selectedOption &&
          onSelect(
            { name: selectedOption.category.indexName, id: selectedOption.category.indexId },
            selectedOption.suggestedNumLags,
          );
      }
    },
    [combinedOptions, customSelectedProxy, sortedProxyOptions, onSelect],
  );

  return (
    <div className={`flex flex-col mt-3 ${hasLayeredProxyFF ? 'mx-[-24px]' : ''}`}>
      <RadioGroup
        name="bulk-proxy-group"
        value={combinedOptions[selectedIndex]?.value ?? combinedOptions[0]?.value ?? ''}
        onValueChange={onCategoryChange}
        className="flex flex-col gap-0"
      >
        {combinedOptions.map(({ value, element, disabled, showRadio = true, label, index }) =>
          showRadio ? (
            <div
              key={value}
              className={cn(
                'border border-solid border-venn-grey-30 border-b-0 p-[12px] hover:bg-venn-switch-light last:border-b mr-0 cursor-pointer',
                hasLayeredProxyFF && 'border-x-0 px-[36px]',
              )}
              role="button"
              tabIndex={-1}
              onClick={() => {
                if (!disabled) {
                  onCategoryChange(value);
                }
              }}
              onKeyDown={() => {}}
            >
              <div className="flex gap-[9px] items-start">
                <RadioGroupItem
                  value={value}
                  id={`bulk-proxy-${value}`}
                  aria-labelledby={`bulk-proxy-label-${value}`}
                  aria-label={label ?? value}
                  disabled={disabled}
                  data-testid={`qa-bulk-proxy-group-${index}`}
                  state={
                    value === (combinedOptions[selectedIndex]?.value ?? combinedOptions[0]?.value)
                      ? 'checked'
                      : 'unchecked'
                  }
                  className="mt-[2px]"
                />
                <div id={`bulk-proxy-label-${value}`} className="flex-1">
                  {element}
                </div>
              </div>
            </div>
          ) : (
            <div key={value}>{element}</div>
          ),
        )}
      </RadioGroup>
    </div>
  );
};

const useProxyOptions = (
  investments: FundToBulkProxy[],
  proxyType?: ProxyTypeEnum,
  numLags?: number,
  firstLayerProxyMeta?: FirstLayerProxyMeta,
): ProxyOption[] => {
  const proxySetId = useRecoilValue(proxySetIdState);
  const queries = useMemo(() => {
    if (!investments.length || !proxyType) {
      return [];
    }

    return investments.map(({ id }) => ({
      queryKey: createProxyOptionsKey(id, proxyType, numLags ?? 0, firstLayerProxyMeta?.proxyId),
      queryFn: () => {
        if (firstLayerProxyMeta) {
          const proxyMeta: ProxyMeta = {
            fundId: id,
            proxyId: firstLayerProxyMeta.proxyId,
            proxyType: firstLayerProxyMeta.proxyType,
            proxyVariant: 'RETURN',
            proxyOrder: 0,
            numLags: firstLayerProxyMeta.numLags,
            extrapolate: firstLayerProxyMeta.extrapolate,
            source: 'APP',
            proxySetId: proxySetId ?? null,
          };
          return getLayeredProxyOptionsForFund(id, proxyType, numLags ?? 0, proxySetId, [proxyMeta]).then(
            (response) => response.content,
          );
        }
        return getProxyOptionsForFund(id, proxyType, numLags ?? 0, proxySetId).then((response) => response.content);
      },
      onError: (error: Parameters<typeof logExceptionIntoSentry>[0]) => {
        Notifications.notify('Unable to load suggested proxy categories', NotificationType.ERROR);
        logExceptionIntoSentry(error);
      },
      suspense: true,
    }));
  }, [investments, proxyType, numLags, proxySetId, firstLayerProxyMeta]);

  const results = useQueries({ queries });

  const sortedProxyOptions = useMemo(() => {
    const proxyOptions = results.reduce<ProxyOption[]>((acc, result) => {
      if (result.data) {
        if (acc.length === 0) {
          return result.data;
        }
        return intersectionBy(acc, result.data, 'category.indexId');
      }

      return acc;
    }, []);

    return proxyOptions.sort(sortCategoryOptions);
  }, [results]);

  return sortedProxyOptions;
};

const useIsCategoryProxy = (initialProxy: SelectedProxy | null, options: RadioOption[]): boolean => {
  return options.some((option) => option.value === initialProxy?.id);
};

export const BulkCategoryOptions = withSuspense(<ShimmerFallback />, React.memo(InternalBulkCategoryOptions));
